import {useGetParticipationQuery} from '@dms/api/core';
import {EntityResourceIds} from '@dms/api/shared';
import {usePermissions} from '@dms/shared';
import {PaymentStateEnum} from '@dms/teas';

import {TestIdProps} from 'shared/web';

import {CheckoutOrderBillingProps} from '../../types/CheckoutOrderBillingProps';
import {CheckoutOrderBillingDetail} from './CheckoutOrderBillingDetail';
import {CheckoutSelectOrderBilling} from './CheckoutSelectOrderBilling';

export const CheckoutOrderBilling = ({
  checkoutContractsInformation,
  order,
  ...props
}: CheckoutOrderBillingProps & TestIdProps) => {
  const issued = !order.payments.every(
    (payment) => payment.paymentState === PaymentStateEnum.CONCEPT
  );

  const {data: businessCaseParticipation} = useGetParticipationQuery({
    recordId: props.businessCaseId,
    resourceId: EntityResourceIds.businessCase,
  });

  const [
    hasSelectBusinessCaseSellingCheckoutContractInformationPermission,
    hasSelectBusinessCasePurchaseCheckoutDeputyPersonPermission,
    hasSelectBusinessCasePurchaseCheckoutContractInformationPermission,
    hasSelectBusinessCaseSellingCheckoutDeputyPersonPermission,
    canEditContractInformation,
  ] = usePermissions({
    permissionKeys: [
      'selectBusinessCaseSellingCheckoutContractInformation',
      'selectBusinessCasePurchaseCheckoutDeputyPerson',
      'selectBusinessCasePurchaseCheckoutContractInformation',
      'selectBusinessCaseSellingCheckoutDeputyPerson',
      'editContractInformation',
    ],
    scopes: {
      selectBusinessCaseSellingCheckoutContractInformation: {
        participation: businessCaseParticipation,
        branchId: props.businessCase?.branchId,
      },
      selectBusinessCasePurchaseCheckoutDeputyPerson: {
        participation: businessCaseParticipation,
        branchId: props.businessCase?.branchId,
      },
      selectBusinessCasePurchaseCheckoutContractInformation: {
        participation: businessCaseParticipation,
        branchId: props.businessCase?.branchId,
      },
      selectBusinessCaseSellingCheckoutDeputyPerson: {
        participation: businessCaseParticipation,
        branchId: props.businessCase?.branchId,
      },
    },
  });

  const canSelectDeputyPerson =
    order.orderDiscriminator === 'SALE'
      ? hasSelectBusinessCaseSellingCheckoutDeputyPersonPermission
      : hasSelectBusinessCasePurchaseCheckoutDeputyPersonPermission;

  const canSelectContract =
    order.orderDiscriminator === 'SALE'
      ? hasSelectBusinessCaseSellingCheckoutContractInformationPermission
      : hasSelectBusinessCasePurchaseCheckoutContractInformationPermission;

  if (issued || props.readonly || (!canSelectDeputyPerson && !canSelectContract)) {
    return (
      <CheckoutOrderBillingDetail
        checkoutContractsInformation={checkoutContractsInformation}
        order={order}
        hasContractSelectDisabled={!canSelectContract}
        hasDeputyPersonSelectDisabled={!canSelectDeputyPerson}
        hasEditContractInformationDisabled={!canEditContractInformation}
        {...props}
        data-testid={props['data-testid']}
      />
    );
  }

  return (
    <CheckoutSelectOrderBilling
      checkoutContractsInformation={checkoutContractsInformation}
      order={order}
      hasDeputyPersonSelectDisabled={!canSelectDeputyPerson}
      hasContractSelectDisabled={!canSelectContract}
      hasEditContractInformationDisabled={!canEditContractInformation}
      {...props}
      data-testid={props['data-testid']}
    />
  );
};
